import { useEffect, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { TradingView } from "../../../components/ui/TradingView";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { Bot, TradingViewLaunchProps } from "../../../_app/types";
import { Order } from "../../../class/Order";
import { RootState } from "../../../store/store";
import { _roundDec } from "../../../utils/_roundToDecimals";
import { precision } from "../../../_utils/precision";
import { calculateStepsCount } from "../../../utils/utils";
import PageLayout from "../../../_components/PageLayout";
import Header from "../../../_modules/Header/Header";
import DottedTextField from "../../../_components/DottedTextField";
import { NBSP } from "../../../_app/consts";
import formatNumber from "../../../_utils/formatNumber";
import {
  getAverageProfitOrders,
  printPriceStepRange,
  printProfitPerOrderRange,
} from "../../../utils/calcBotCreationFields";
import BadgeDirection from "../../../_components/BadgeDirection";
import CustomPaper from "../../../_components/CustomPaper";
import Spacer from "../../../_components/Spacer";
import FuturesBotLaunch from "./FuturesBotLaunch";

interface Props {
  bot: Bot;
  showPreview: (value: boolean) => void;
  stepInBase: number;
  stepInQuote: number;
  stepsCount: number;
  tradingViewProperties: TradingViewLaunchProps;
  orderList: Order[];
}

const FuturesBotPreviewPage = ({
  bot,
  showPreview,
  stepInBase,
  stepInQuote,
  stepsCount,
  tradingViewProperties,
  orderList,
}: Props) => {
  const theme = useTheme();
  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);
  const user = useSelector((state: RootState) => state.user);

  const profitRatio = 500;

  const orderSize = useMemo(() => {
    let steps =
      bot.currencyType === "BASE"
        ? Number((bot.investment / stepInBase).toFixed(0))
        : Number((bot.investment / stepInQuote).toFixed(0));
    return _roundDec(bot.ticker.orderStep * _roundDec(steps, 0), precision(bot.ticker.orderStep));
  }, [bot]);

  useEffect(() => {
    // console.log("botinvestment", bot.investment);
    // console.log("bot.investment / stepInBase", bot.investment / stepInBase);
    // console.log("orderSize", orderSize);
  }, [bot]);

  const STEPS_COUNT = useMemo(() => {
    return calculateStepsCount(
      bot,
      stepInBase,
      stepInQuote,
      bot.currencyType === "BASE" ? bot.investment / stepInBase : bot.investment / stepInQuote
    );
  }, [bot.currencyType, bot.investment, stepInBase, stepInQuote]);

  const { average } = useMemo(() => {
    const result = orderList.reduce<{ sum: number; count: number }>(
      (acc, item) => {
        const value = bot.gridType === "ARITHMETIC" ? item.priceStepPercent * 100 : item.priceStep;
        acc.sum += value;
        acc.count += 1;
        return acc;
      },
      { sum: 0, count: 0 }
    );

    const average = result.count > 0 ? result.sum / result.count : 0;

    return { average };
  }, [orderList]);

  const { min, max } = useMemo(() => {
    const result = orderList.reduce<{ min: number; max: number }>(
      (acc, item) => {
        const value = bot.gridType === "ARITHMETIC" ? item.priceStepPercent * 100 : item.priceStep;
        acc.min = Math.min(acc.min, value);
        acc.max = Math.max(acc.max, value);
        return acc;
      },
      { min: Infinity, max: -Infinity }
    );
    return result;
  }, [orderList]);

  // custom grid mode
  const minOrderAmount = useMemo(() => {
    return Math.min(...bot.customGridSettings.flatMap((item) => item.amount));
  }, [bot.customGridSettings]);

  return (
    <Box
      sx={{
        position: "fixed",
        zIndex: "1000",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        background:
          theme.palette.mode === "dark" ? "rgba(13, 16, 18, 1)" : "rgba(245, 245, 245, 1)",
      }}
    >
      <PageLayout
        header={<Header pageTitle={"Futures bot preview"} needBack onChange={showPreview} />}
        bottomButton={
          <FuturesBotLaunch
            bot={bot}
            activeAccount={user.activeAccount}
            stepsCount={stepsCount}
            stepInQuote={stepInQuote}
            stepInBase={stepInBase}
          />
        }
        contentSx={{ padding: "20px 8px 0" }}
      >
        <Box sx={{ marginBottom: 3 }}>
          <TradingView
            background={theme.palette.background.default}
            headerColor={theme.palette.background.default}
            ChartProperties={tradingViewProperties}
          />
        </Box>
        <Stack direction={"column"} alignItems={"center"} spacing={1} sx={{ marginTop: 2 }}>
          <Stack sx={{ width: "100%" }} direction={"column"} alignItems={"center"} spacing={2}>
            <Stack style={{ width: "100%" }} spacing={2} sx={{ marginTop: 2 }}>
              <Typography variant="h2" component="div" color={"textSecondary"}>
                Bot settings
              </Typography>
              <Stack sx={{ width: "100%" }} direction={"column"} alignItems={"center"} spacing={1}>
                <DottedTextField
                  placeholder={`Bot${NBSP}type`}
                  value={`Futures${NBSP}grid`}
                />
                <DottedTextField

                  placeholder={"Account"}
                  value={user.activeAccount.name}
                />
                <DottedTextField

                  placeholder={"Pair"}
                  value={`${bot.base}${NBSP}/${NBSP}${bot.quote}`}
                />
                <DottedTextField

                  placeholder={"Investment"}
                  value={`${formatNumber(bot.investment)} ${bot.currencyType === "BASE" ? bot.base : bot.quote}`}
                />
                {CustomGridMode ? (
                  <DottedTextField placeholder={`Order size`} value={`$${minOrderAmount}`} />
                ) : (
                  <DottedTextField

                    placeholder={"Order size"}
                    value={`${formatNumber(orderSize, precision(bot.ticker.orderStep))} ${bot.base}`}
                  />
                )}
                {CustomGridMode ? (
                  <DottedTextField

                    placeholder={`Profit per order`}
                    value={printProfitPerOrderRange(bot.customGridSettings)}
                  />
                ) : (
                  <DottedTextField
                    placeholder={`Profit per order`}

                    value={
                      bot.gridType === "ARITHMETIC"
                        ? `$${formatNumber(bot.priceStep * bot.ticker.orderStep * STEPS_COUNT)}`
                        : `$${formatNumber(min * bot.ticker.orderStep * STEPS_COUNT)}
                        - $${formatNumber(max * bot.ticker.orderStep * STEPS_COUNT)}`
                    }
                  />
                )}
                <DottedTextField

                  placeholder={"Required margin"}
                  value={`${formatNumber(bot.currencyType === "BASE"
                    ? (STEPS_COUNT * stepInQuote) / bot.leverage
                    : bot.investment / bot.leverage, 2)} ${bot.quote}`}
                />
                <DottedTextField fullWidth placeholder={"Leverage"} value={`${bot.leverage}x`} />
                <DottedTextField

                  placeholder={"Direction"}
                  value={``}
                  children={<BadgeDirection gridDirection={bot.gridDirection} />}
                />
                <DottedTextField

                  placeholder={"Min price"}
                  value={`${formatNumber(bot.minPrice, precision(bot.ticker.priceStep))} ${bot.quote}`}
                />
                <DottedTextField

                  placeholder={"Max price"}
                  value={`${formatNumber(bot.maxPrice, precision(bot.ticker.priceStep))} ${bot.quote}`}
                />
              </Stack>
            </Stack>
            <Stack sx={{ width: "100%" }} direction={"column"} alignItems={"center"} spacing={1}>
              {CustomGridMode ? (
                <DottedTextField

                  placeholder={"Grids"}
                  value={`${bot.customGridSettings.length} (${bot.gridType})`}
                />
              ) : (
                <DottedTextField

                  placeholder={"Grids"}
                  value={`${bot.gridsAmount}${NBSP}(${bot.gridType})`}
                />
              )}

              {CustomGridMode ? (
                <DottedTextField

                  placeholder={`Price${NBSP}step, %`}
                  value={printPriceStepRange(bot.customGridSettings)}
                />
              ) : (
                <>
                  {bot.gridType === "ARITHMETIC" && (
                    <DottedTextField

                      placeholder={"Price step, %"}
                      value={bot.priceStepPercentage}
                    />
                  )}
                  {bot.gridType === "GEOMETRIC" && (
                    <DottedTextField

                      placeholder={"Price step, $"}
                      value={bot.priceStepPercentage}
                    />
                  )}
                </>
              )}
              {/* Price step */}
              {!CustomGridMode && (
                <>
                  <DottedTextField

                    placeholder={"Price step"}
                    value={`${formatNumber(
                      bot.gridType === "ARITHMETIC" ? bot.priceStep : bot.priceStep * 100,
                      bot.gridType === "ARITHMETIC" ? precision(bot.ticker.priceStep) : 2
                    )}
										${bot.gridType === "ARITHMETIC" ? bot.quote : "%"}`}
                  />
                </>
              )}
            </Stack>
          </Stack>
          <Box sx={{ width: "100%", marginTop: "10px !important" }}>
            <CustomPaper>
              <Stack direction={"column"} spacing={1}>
                {CustomGridMode && (
                  <DottedTextField
                    placeholder={`Exp. profit per grid`}
                    withoutDotted
                    value={formatNumber(
                      getAverageProfitOrders(bot.customGridSettings) * profitRatio
                    )}
                  />
                )}
                {!CustomGridMode && (
                  <DottedTextField
                    placeholder={"Exp. profit per grid"}
                    withoutDotted
                    value={
                      bot.gridType === "ARITHMETIC"
                        ? `$${formatNumber(bot.priceStep * bot.ticker.orderStep * STEPS_COUNT * profitRatio)}`
                        : `$${formatNumber(average * bot.ticker.orderStep * STEPS_COUNT * profitRatio)}`
                    }
                  />
                )}
              </Stack>
            </CustomPaper>
          </Box>
        </Stack>
        <Spacer height="100px" />
      </PageLayout>
    </Box>
  );
};

export default FuturesBotPreviewPage;
